﻿import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../foundation/altzero_base/base/components/ui/select';
import { Slider } from '../../../foundation/altzero_base/base/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../foundation/altzero_base/base/components/ui/table';
import {
    ArrowLeft,
    Search,
    Zap,
    TrendingUp,
    MapPin,
    DollarSign,
    AlertCircle,
    Loader2,
    Clock,
    Maximize2,
    Minimize2,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    PanelLeftClose,
    PanelLeft,
    Trophy,
    Award,
    Filter,
    X
} from 'lucide-react';
import ScopingAILayout from '../components/ScopingAILayout';
import DetailedOfferAnalysis from '../components/DetailedOfferAnalysis';
import DetailedRateInput from '../components/DetailedRateInput';
import { getHistoricalOffers } from '../services/tenderService';
import { format } from 'date-fns';
import { formatTermLabel, formatTermKeyLabel } from '../lib/termLabel';
import { ENERGY_RATE_DECIMALS } from '../lib/ratePrecision';
import {
    RateTypeData,
    createEmptyMultiYearRates,
    createEmptyEnvironmentalCharges,
    createEmptyNonEnergyCharges
} from '../types/offerTypes';

// Constants for usage filter range
const MIN_USAGE = 0;
const MAX_USAGE = 500000;

const QuickProposalPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [searched, setSearched] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [expandedOfferId, setExpandedOfferId] = useState<string | null>(null);

    // Form State
    const [postcode, setPostcode] = useState('');
    const [annualUsage, setAnnualUsage] = useState<string>('15000');
    const [contractTerm, setContractTerm] = useState<string>('12');
    const [limit, setLimit] = useState<string>('5');

    // Usage Filter State
    const [minUsage, setMinUsage] = useState<string>('');
    const [maxUsage, setMaxUsage] = useState<string>('');
    const [sliderValues, setSliderValues] = useState<number[]>([MIN_USAGE, MAX_USAGE]);

    // Handler for slider value changes
    const handleSliderChange = (values: number[]) => {
        setSliderValues(values);
        setMinUsage(values[0].toString());
        setMaxUsage(values[1].toString());
    };

    // Handler for min input changes
    const handleMinUsageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string or numeric values only
        if (value === '' || /^\d+$/.test(value)) {
            setMinUsage(value);
            if (value !== '') {
                const numValue = parseInt(value);
                setSliderValues([numValue, sliderValues[1]]);
            }
        }
    };

    // Handler for max input changes
    const handleMaxUsageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string or numeric values only
        if (value === '' || /^\d+$/.test(value)) {
            setMaxUsage(value);
            if (value !== '') {
                const numValue = parseInt(value);
                setSliderValues([sliderValues[0], numValue]);
            }
        }
    };

    // Handler to clear usage filter
    const handleClearUsageFilter = () => {
        setMinUsage('');
        setMaxUsage('');
        setSliderValues([MIN_USAGE, MAX_USAGE]);
    };

    // Detailed Current Rates State
    const [currentRates, setCurrentRates] = useState<RateTypeData>({
        peakRates: createEmptyMultiYearRates(),
        offpeakRates: createEmptyMultiYearRates(),
        shoulderRates: createEmptyMultiYearRates(),
        environmentalCharges: createEmptyEnvironmentalCharges(),
        nonEnergyCharges: createEmptyNonEnergyCharges()
    });

    // Helper to safely extract rate value
    const extractRate = (val: any): number => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') return parseFloat(val) || 0;
        if (typeof val === 'object' && val !== null) {
            return val.rate || val.ratePerKwh || val.value || 0;
        }
        return 0;
    };

    // Calculate cost based on detailed rates
    const calculateCostFromRates = (rates: RateTypeData, usageStr: string) => {
        const usage = parseFloat(usageStr) || 0;
        if (usage === 0) return 0;

        // Energy Cost (Year 1)
        const peakRate = rates.peakRates.year_1 || 0;
        const offPeakRate = rates.offpeakRates.year_1 || 0;

        // Simple 60/40 split assumption for now if both exist, otherwise 100% peak
        let energyCost = 0;
        if (peakRate > 0 && offPeakRate > 0) {
            const peakUsage = usage * 0.6;
            const offPeakUsage = usage * 0.4;
            energyCost = ((peakUsage * peakRate) + (offPeakUsage * offPeakRate)) / 100;
        } else {
            energyCost = (usage * (peakRate || offPeakRate)) / 100;
        }

        // Environmental Cost (Year 1)
        let envRate = 0;
        if (rates.environmentalCharges) {
            Object.values(rates.environmentalCharges).forEach(charge => {
                if (charge?.year_1) {
                    envRate += charge.year_1.ratePerKwh || 0;
                }
            });
        }
        const envCost = (usage * envRate) / 100;

        // Non-Energy Charges (Annual $)
        let fixedCost = 0;
        if (rates.nonEnergyCharges) {
            Object.values(rates.nonEnergyCharges).forEach(val => {
                fixedCost += val || 0;
            });
        }

        return energyCost + envCost + fixedCost;
    };

    const currentAnnualCost = calculateCostFromRates(currentRates, annualUsage);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!postcode) return;

        setLoading(true);
        setSearched(true);
        setOffers([]);
        setExpandedOfferId(null);

        try {
            const term = parseInt(contractTerm);
            const limitNum = parseInt(limit);

            // Parse usage filter values (convert empty string to undefined)
            const minUsageNum = minUsage !== '' ? parseInt(minUsage) : undefined;
            const maxUsageNum = maxUsage !== '' ? parseInt(maxUsage) : undefined;

            const result = await getHistoricalOffers(postcode, limitNum, term, minUsageNum, maxUsageNum);

            if (result.success && result.data) {
                const processedOffers = result.data.map(offer => {
                    // Calculate estimated costs
                    const usageNum = parseFloat(annualUsage) || 0;
                    let estimatedCost = 0;
                    let peakRate = 0;
                    let offPeakRate = 0;
                    let dailySupply = 0;

                    // Try to use detailed multi-year data first (Year 1)
                    if (offer.multiYearRateData) {
                        const data = offer.multiYearRateData.smooth || offer.multiYearRateData.stepped;
                        if (data) {
                            // Use the detailed calculation logic if data exists
                            // We need to map the offer data to RateTypeData structure if possible
                            // Or just use the simplified extraction for now as fallback

                            // Base Energy Rates
                            peakRate = extractRate(data.peakRates?.year_1);
                            offPeakRate = extractRate(data.offpeakRates?.year_1) || peakRate;

                            // Daily Supply / Fixed Charges
                            // Sum up non-energy charges for daily supply equivalent
                            let totalFixed = 0;
                            if (data.nonEnergyCharges) {
                                Object.values(data.nonEnergyCharges).forEach((val: any) => totalFixed += (val || 0));
                            }
                            dailySupply = (totalFixed / 365) * 100; // Convert back to c/day for display

                            // Add Environmental Charges to Energy Rates
                            if (data.environmentalCharges) {
                                let totalEnvRate = 0;
                                Object.values(data.environmentalCharges).forEach((charge: any) => {
                                    if (charge?.year_1) {
                                        totalEnvRate += extractRate(charge.year_1);
                                    }
                                });
                                peakRate += totalEnvRate;
                                offPeakRate += totalEnvRate;
                            }
                        }
                    }

                    // Fallback to simple rates if still 0
                    if (peakRate === 0) {
                        peakRate = extractRate(offer.rates?.peak);
                        offPeakRate = extractRate(offer.rates?.offpeak) || peakRate;
                        dailySupply = extractRate(offer.rates?.daily);
                    }

                    if (usageNum > 0) {
                        const peakUsage = usageNum * 0.6;
                        const offPeakUsage = usageNum * 0.4;
                        const usageCost = ((peakUsage * peakRate) + (offPeakUsage * offPeakRate)) / 100;
                        const supplyCost = (dailySupply * 365) / 100;
                        estimatedCost = usageCost + supplyCost;
                    }

                    return {
                        ...offer,
                        estimatedAnnualCost: estimatedCost,
                        displayRates: {
                            peak: peakRate,
                            offpeak: offPeakRate,
                            daily: dailySupply
                        }
                    };
                });

                // Sort by Estimated Cost (Cheapest First)
                processedOffers.sort((a, b) => {
                    if (a.estimatedAnnualCost === 0) return 1;
                    if (b.estimatedAnnualCost === 0) return -1;
                    return a.estimatedAnnualCost - b.estimatedAnnualCost;
                });

                // Assign Ranks
                const rankedOffers = processedOffers.map((offer, index) => ({
                    ...offer,
                    rank: index + 1
                }));

                setOffers(rankedOffers);
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper to format currency
    const formatCurrency = (val: number | string) => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (isNaN(num) || num === 0) return '-';
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(num);
    };

    // Helper to format rate. At least 4 dp, at most ENERGY_RATE_DECIMALS — `toFixed(4)` here
    // capped the display below the 5 dp the rate path collects, so AGL's 5th decimal was
    // rounded away. Only peak / off-peak / daily energy rates reach this; environmental rates
    // need 9 dp and are formatted by formatEnvRate instead. See ratePrecision.ts.
    const formatRate = (val: number | string, unit: string) => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (isNaN(num) || num === 0) return '-';
        const formatted = num.toLocaleString('en-AU', {
            minimumFractionDigits: 4,
            maximumFractionDigits: ENERGY_RATE_DECIMALS,
        });
        return `${formatted} ${unit}`;
    };

    // Helper to format usage
    const formatUsage = (val: number | null | undefined) => {
        if (val === null || val === undefined) return 'N/A';
        return `${val.toLocaleString('en-AU')} kWh`;
    };

    const toggleOfferExpansion = (offerId: string) => {
        setExpandedOfferId(expandedOfferId === offerId ? null : offerId);
    };

    return (
        <ScopingAILayout>
            <div className="w-full max-w-full overflow-hidden">
                <div className="flex flex-col w-full h-[calc(100vh-5rem)] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                    {/* Header */}
                    <div className="bg-white border-b border-gray-200 px-3 md:px-6 py-3 md:py-4 shrink-0 z-30 w-full overflow-hidden">
                        <div className="flex items-center justify-between gap-2 md:gap-4 w-full min-w-0">
                            <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-shrink overflow-hidden">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/tender/new-proposal')}
                                    className="text-gray-500 hover:text-gray-900 shrink-0"
                                >
                                    <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
                                    <span className="hidden md:inline">Back</span>
                                </Button>
                                <div className="h-6 w-px bg-gray-300 hidden md:block" />
                                <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                                    <div className="p-1.5 md:p-2 bg-orange-100 rounded-lg shrink-0">
                                        <Zap className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                                    </div>
                                    <div className="min-w-0 overflow-hidden">
                                        <h1 className="text-sm md:text-lg font-bold text-gray-900 truncate">Side by Side Comparison</h1>
                                        <p className="text-[10px] md:text-xs text-gray-500 hidden sm:block truncate">Instant market comparison</p>
                                    </div>
                                </div>
                            </div>

                            {/* Current Cost Summary in Header */}
                            {currentAnnualCost > 0 && (
                                <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                                    <div className="text-right">
                                        <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wider">Current Est. Cost</p>
                                        <p className="text-lg font-bold text-blue-900 leading-none">{formatCurrency(currentAnnualCost)}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative w-full">

                        {/* Left Panel - Input Form */}
                        <div className="w-1/2 bg-white border-r border-gray-200 overflow-hidden z-30 flex flex-col shrink-0">
                            <div className="w-full h-full overflow-y-auto custom-scrollbar">
                                <div className="p-6 space-y-6 flex-1">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900 mb-1">Client Site Details</h2>
                                            <p className="text-sm text-gray-500">Enter current site details to compare.</p>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={handleSearch} className="space-y-6">
                                    {/* Location & Usage */}
                                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                            <MapPin className="w-4 h-4" /> Location & Usage
                                        </h3>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="postcode" className="text-xs">Postcode</Label>
                                                <Input
                                                    id="postcode"
                                                    placeholder="3000"
                                                    value={postcode}
                                                    onChange={(e) => setPostcode(e.target.value)}
                                                    required
                                                    className="bg-white"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="term" className="text-xs">Term (Months)</Label>
                                                <Select value={contractTerm} onValueChange={setContractTerm}>
                                                    <SelectTrigger className="bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="12">12 Months</SelectItem>
                                                        <SelectItem value="24">24 Months</SelectItem>
                                                        <SelectItem value="36">36 Months</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="limit" className="text-xs">Number of Offers</Label>
                                                <Select value={limit} onValueChange={setLimit}>
                                                    <SelectTrigger className="bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="5">Latest 5 Offers</SelectItem>
                                                        <SelectItem value="10">Latest 10 Offers</SelectItem>
                                                        <SelectItem value="15">Latest 15 Offers</SelectItem>
                                                        <SelectItem value="20">Latest 20 Offers</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="h-4"></div>
                                                <Button
                                                    type="submit"
                                                    className="w-full bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Searching...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Search className="w-4 h-4 mr-2" />
                                                            Find Offers
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filter by Annual Usage */}
                                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                                <Filter className="w-4 h-4" /> Filter by Annual Usage
                                            </h3>
                                            {(minUsage !== '' || maxUsage !== '') && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleClearUsageFilter}
                                                    className="h-7 text-xs text-gray-500 hover:text-gray-900"
                                                >
                                                    <X className="w-3 h-3 mr-1" />
                                                    Clear
                                                </Button>
                                            )}
                                        </div>

                                        <div className="space-y-4">
                                            {/* Dual-handle Slider */}
                                            <div className="px-2 pt-2">
                                                <Slider
                                                    value={sliderValues}
                                                    onValueChange={handleSliderChange}
                                                    min={MIN_USAGE}
                                                    max={MAX_USAGE}
                                                    step={1000}
                                                    className="w-full"
                                                />
                                                <div className="flex justify-between mt-2 text-xs text-gray-500">
                                                    <span>{MIN_USAGE.toLocaleString('en-AU')} kWh</span>
                                                    <span>{MAX_USAGE.toLocaleString('en-AU')} kWh</span>
                                                </div>
                                            </div>

                                            {/* Min and Max Input Fields */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="minUsage" className="text-xs">Min Usage (kWh)</Label>
                                                    <Input
                                                        id="minUsage"
                                                        type="text"
                                                        placeholder={MIN_USAGE.toString()}
                                                        value={minUsage}
                                                        onChange={handleMinUsageChange}
                                                        className="bg-white"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="maxUsage" className="text-xs">Max Usage (kWh)</Label>
                                                    <Input
                                                        id="maxUsage"
                                                        type="text"
                                                        placeholder={MAX_USAGE.toString()}
                                                        value={maxUsage}
                                                        onChange={handleMaxUsageChange}
                                                        className="bg-white"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Detailed Current Rates Input */}
                                    <DetailedRateInput
                                        data={currentRates}
                                        onChange={setCurrentRates}
                                    />
                                </form>
                            </div>
                        </div>

                        {/* Right Panel - Offers Table */}
                        <div className="flex-1 bg-slate-50 overflow-hidden relative flex flex-col min-w-0">


                            {!searched ? (
                                <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                                        <Search className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Compare</h3>
                                    <p className="text-gray-500 max-w-sm">
                                        Enter site details and search to see a side-by-side comparison with market offers.
                                    </p>
                                </div>
                            ) : offers.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center">
                                    <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                                        <AlertCircle className="w-8 h-8 text-orange-500" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Offers Found</h3>
                                    <p className="text-gray-500 max-w-sm">
                                        No historical offers found for {postcode} with a {formatTermKeyLabel(contractTerm)} term.
                                    </p>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-auto p-6">
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                        <Table>
                                            <TableHeader className="bg-gray-50">
                                                <TableRow>
                                                    <TableHead className="w-[50px]"></TableHead>
                                                    <TableHead>Retailer</TableHead>
                                                    <TableHead>Annual Usage</TableHead>
                                                    <TableHead>Term</TableHead>
                                                    <TableHead className="text-right">Est. Annual Cost</TableHead>
                                                    <TableHead className="text-right">Savings</TableHead>
                                                    <TableHead className="text-center">Rank</TableHead>
                                                    <TableHead className="w-[50px]"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {offers.map((offer) => {
                                                    const savings = currentAnnualCost > 0 ? currentAnnualCost - offer.estimatedAnnualCost : 0;
                                                    const isExpanded = expandedOfferId === offer.id;
                                                    const isCheapest = offer.rank === 1;

                                                    return (
                                                        <React.Fragment key={offer.id}>
                                                            <TableRow
                                                                className={`cursor-pointer hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`}
                                                                onClick={() => toggleOfferExpansion(offer.id)}
                                                            >
                                                                <TableCell>
                                                                    {offer.retailerLogo ? (
                                                                        <img src={offer.retailerLogo} className="w-8 h-8 object-contain" alt="" />
                                                                    ) : (
                                                                        <Zap className="w-6 h-6 text-orange-500" />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="font-medium text-gray-900">{offer.retailerName || 'Unknown'}</div>
                                                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                                                        <Clock className="w-3 h-3" />
                                                                        {offer.createdAt ? format(new Date(offer.createdAt), 'dd MMM yyyy') : 'New'}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className={offer.siteAnnualKwh === null || offer.siteAnnualKwh === undefined ? 'text-gray-400' : ''}>
                                                                        {formatUsage(offer.siteAnnualKwh)}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge variant="secondary" className="bg-white border border-gray-200">
                                                                        {formatTermLabel(offer.termMonths)}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    <div className={`font-bold ${isCheapest ? 'text-orange-700' : 'text-gray-900'}`}>
                                                                        {formatCurrency(offer.estimatedAnnualCost)}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {savings > 0 ? (
                                                                        <div className="flex items-center justify-end gap-1 text-green-600 font-medium">
                                                                            <TrendingUp className="w-3 h-3" />
                                                                            {formatCurrency(savings)}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-gray-400 text-sm">-</span>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-center">
                                                                    {isCheapest ? (
                                                                        <div className="flex items-center justify-center">
                                                                            <div className="bg-orange-100 text-orange-700 p-1 rounded-full">
                                                                                <Trophy className="w-4 h-4" />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-gray-500 text-sm">#{offer.rank}</span>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>

                                                            {/* Expanded Details Row */}
                                                            <AnimatePresence>
                                                                {isExpanded && (
                                                                    <TableRow className="bg-gray-50/50 hover:bg-gray-50/50 border-t-0">
                                                                        <TableCell colSpan={7} className="p-0">
                                                                            <motion.div
                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                className="overflow-hidden"
                                                                            >
                                                                                <div className="p-6 border-b border-gray-200">
                                                                                    <div className="flex flex-col gap-6">
                                                                                        <div className="w-full">
                                                                                            <h4 className="font-semibold text-gray-900 mb-4">Detailed Breakdown</h4>
                                                                                            {offer.multiYearRateData ? (
                                                                                                <DetailedOfferAnalysis data={offer.multiYearRateData} />
                                                                                            ) : (
                                                                                                <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                                                                                                    No detailed rate data available for this offer.
                                                                                                </div>
                                                                                            )}
                                                                                        </div>

                                                                                        {/* Quick Stats Sidebar - Moved to Bottom */}
                                                                                        <div className="w-full">
                                                                                            <Card className="bg-white border-gray-200">
                                                                                                <CardContent className="p-4">
                                                                                                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Rate Summary (Yr 1)</h5>
                                                                                                    <div className="grid grid-cols-3 gap-4">
                                                                                                        <div className="flex flex-col">
                                                                                                            <span className="text-xs text-gray-600">Peak</span>
                                                                                                            <span className="font-medium text-sm">{formatRate(offer.displayRates.peak, 'c/kWh')}</span>
                                                                                                        </div>
                                                                                                        <div className="flex flex-col">
                                                                                                            <span className="text-xs text-gray-600">Off-Peak</span>
                                                                                                            <span className="font-medium text-sm">{formatRate(offer.displayRates.offpeak, 'c/kWh')}</span>
                                                                                                        </div>
                                                                                                        <div className="flex flex-col">
                                                                                                            <span className="text-xs text-gray-600">Daily</span>
                                                                                                            <span className="font-medium text-sm">{formatRate(offer.displayRates.daily, 'c/day')}</span>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </CardContent>
                                                                                            </Card>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </motion.div>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </AnimatePresence>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ScopingAILayout>
    );
};

export default QuickProposalPage;
